import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Trash2, Plus, BookOpen, CheckCircle2, Circle } from 'lucide-react';
import api from '../../services/api';
import { useConfirmation } from '../../context/ConfirmationContext';

export default function GradeDetail() {
  const { gradeId } = useParams();
  const navigate = useNavigate();
  const { showConfirmation } = useConfirmation();

  const [grade, setGrade] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [assigned, setAssigned] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const showToastMessage = (message, type = 'error') => {
    setSuccess('');
    setError('');
    if (type === 'success') setSuccess(message);
    else setError(message);
    setTimeout(() => {
      setSuccess('');
      setError('');
    }, 4000);
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gradeId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/admin/grades/${gradeId}/subjects`);
      if (res.data.success) {
        setGrade(res.data.grade);
        setSubjects(res.data.subjects || []);
        const ids = res.data.subjects
          .filter((s) => s.assigned)
          .map((s) => s.subject_id);
        setAssigned(new Set(ids));
      } else {
        showToastMessage(res.data.message || 'Failed to load grade', 'error');
      }
    } catch (err) {
      showToastMessage('Failed to load grade', 'error');
    } finally {
      setLoading(false);
    }
  };

  const toggleSubject = (id) => {
    setAssigned((prev) => {
      const copy = new Set(prev);
      if (copy.has(id)) copy.delete(id);
      else copy.add(id);
      return copy;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/grades/${gradeId}/subjects`, {
        subject_ids: Array.from(assigned),
      });
      showToastMessage('Subject assignments updated', 'success');
      loadData();
    } catch (err) {
      showToastMessage(err.response?.data?.message || 'Failed to save assignments', 'error');
    } finally {
      setSaving(false);
    }
  };

  const confirmDeleteGrade = () => {
    if (!grade) return;
    showConfirmation({
      title: 'Delete Grade?',
      message: `Are you sure you want to delete "${grade.grade_name}"? This action cannot be undone.`,
      type: 'danger',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          const res = await api.delete(`/admin/grades/${gradeId}`);
          if (res.data.success) {
            navigate('/admin/grades');
          }
        } catch (err) {
          showToastMessage(err.response?.data?.message || 'Failed to delete grade', 'error');
        }
      },
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!grade) {
    return (
      <div className="space-y-6">
        <button
          onClick={() => navigate('/admin/grades')}
          className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
          Grade not found
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <button
        onClick={() => navigate('/admin/grades')}
        className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Grades
      </button>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-green-700">
          {success}
        </div>
      )}

      {/* Header Card */}
      <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl shadow-lg p-8 text-white">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-medium mb-3">
              <BookOpen className="w-4 h-4" />
              Grade Details
            </div>
            <h1 className="text-4xl font-bold mb-2">{grade.grade_name}</h1>
            {grade.description && (
              <p className="text-indigo-100 text-lg">{grade.description}</p>
            )}
            <div className="mt-4 flex items-center gap-6">
              <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg">
                <p className="text-sm text-indigo-100">Total Subjects</p>
                <p className="text-2xl font-bold">{subjects.length}</p>
              </div>
              <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg">
                <p className="text-sm text-indigo-100">Assigned</p>
                <p className="text-2xl font-bold">{assigned.size}</p>
              </div>
            </div>
          </div>
          <button
            onClick={confirmDeleteGrade}
            className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition flex items-center gap-2 shadow-lg"
          >
            <Trash2 className="w-4 h-4" /> Delete Grade
          </button>
        </div>
      </div>

      {/* Subjects Section */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-indigo-600" />
            Assign Subjects
          </h2>
          <p className="text-sm text-gray-600 mt-1">Select subjects to assign to this grade</p>
        </div>
        
        <div className="p-6">
          {subjects.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">No subjects available</p>
              <button
                onClick={() => navigate('/admin/subjects')}
                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
              >
                <Plus className="w-4 h-4" /> Create First Subject
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {subjects.map((s) => (
                  <div
                    key={s.subject_id}
                    onClick={() => toggleSubject(s.subject_id)}
                    className={`relative group cursor-pointer rounded-xl border-2 p-4 transition-all duration-200 ${
                      assigned.has(s.subject_id)
                        ? 'border-indigo-500 bg-indigo-50 shadow-md'
                        : 'border-gray-200 bg-white hover:border-indigo-300 hover:shadow-sm'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className={`font-semibold text-sm mb-1 ${
                          assigned.has(s.subject_id) ? 'text-indigo-900' : 'text-gray-800'
                        }`}>
                          {s.subject_name}
                        </h3>
                        {s.subject_code && (
                          <p className={`text-xs ${
                            assigned.has(s.subject_id) ? 'text-indigo-600' : 'text-gray-500'
                          }`}>
                            {s.subject_code}
                          </p>
                        )}
                      </div>
                      <div className={`flex-shrink-0 transition-transform duration-200 ${
                        assigned.has(s.subject_id) ? 'scale-100' : 'scale-90 opacity-60 group-hover:scale-100 group-hover:opacity-100'
                      }`}>
                        {assigned.has(s.subject_id) ? (
                          <CheckCircle2 className="w-6 h-6 text-indigo-600" />
                        ) : (
                          <Circle className="w-6 h-6 text-gray-400" />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="mt-6 flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-gray-200">
                <button
                  onClick={() => navigate('/admin/subjects')}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                >
                  <Plus className="w-4 h-4" /> Add New Subject
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-sm"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4" />
                      Save Assignments
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
